import React, { useEffect, useMemo, useState } from 'react';
import { theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const riseUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const lineTravel = keyframes`
  to {
    stroke-dashoffset: -140;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
`;

const dotBlink = keyframes`
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.2s ease;
`;

const Card = styled.div`
  width: min(92vw, 460px);
  border-radius: 18px;
  border: 1px solid var(--theme-border-color);
  background: var(--theme-bg-container);
  box-shadow: var(--app-shadow-soft);
  padding: 22px 22px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  animation: ${riseUp} 0.25s ease;
`;

const MedicalGraphic = styled.div`
  width: 220px;
  height: 112px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  .badge {
    position: absolute;
    left: 50%;
    top: 8px;
    transform: translateX(-50%);
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: var(--app-gradient);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--app-shadow-soft);
    animation: ${pulse} 1.2s ease-in-out infinite;

    &::before,
    &::after {
      content: '';
      position: absolute;
      background: #fff;
      border-radius: 2px;
    }

    &::before {
      width: 22px;
      height: 5px;
    }

    &::after {
      width: 5px;
      height: 22px;
    }
  }

  svg {
    width: 220px;
    height: 90px;
    margin-top: 22px;
  }

  .ekg {
    stroke-dasharray: 30 10;
    animation: ${lineTravel} 1.1s linear infinite;
  }
`;

const Title = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: var(--theme-text-color);
  text-align: center;
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: var(--theme-text-color);
  opacity: 0.78;
  text-align: center;
`;

const Dots = styled.div`
  display: inline-flex;
  gap: 5px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--app-accent);
    animation: ${dotBlink} 1.2s infinite ease-in-out;
  }

  span:nth-child(2) {
    animation-delay: 0.15s;
  }

  span:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

function GlobalHealthLoadingOverlay() {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const routerStatus = useRouterState({ select: (s) => s.status });
  const fetchingCount = useIsFetching();
  const mutatingCount = useIsMutating();

  const isBusy = routerStatus === 'pending' || fetchingCount > 0 || mutatingCount > 0;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timer: number | undefined;

    if (isBusy) {
      timer = window.setTimeout(() => {
        setVisible(true);
      }, 180);
    } else {
      setVisible(false);
    }

    return () => {
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [isBusy]);

  const title = useMemo(() => {
    if (routerStatus === 'pending') {
      return t('health_loading_navigation', { defaultValue: 'Opening your care workspace...' });
    }
    if (mutatingCount > 0) {
      return t('health_loading_processing', { defaultValue: 'Updating health records securely...' });
    }
    return t('health_loading_fetching', { defaultValue: 'Loading insurance data...' });
  }, [mutatingCount, routerStatus, t]);

  if (!visible) {
    return null;
  }

  return (
    <Backdrop style={{ background: token.colorBgMask }}>
      <Card>
        <MedicalGraphic>
          <span className="badge" />
          <svg viewBox="0 0 220 90" fill="none" aria-hidden>
            <rect x="10" y="18" width="200" height="58" rx="14" fill="var(--app-accent-soft-3)" stroke="var(--app-accent-soft-1)" />
            <path
              className="ekg"
              d="M20 49h26l8-11 12 24 13-19 10 9h26l8-11 12 24 13-19 10 9h26"
              stroke={token.colorPrimary}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </MedicalGraphic>

        <Title>{title}</Title>
        <Subtitle>
          {t('health_loading_subtitle', {
            defaultValue: 'Preparing your healthcare services and benefits overview'
          })}
        </Subtitle>
        <Dots aria-hidden>
          <span />
          <span />
          <span />
        </Dots>
      </Card>
    </Backdrop>
  );
}

export default GlobalHealthLoadingOverlay;
